import React, { useEffect, useState } from 'react'
import { Drawer, Tabs, Button } from 'antd'
import Select from 'react-select'
import * as ECT from '@whoicd/icd11ect'
import { Route, Switch, useHistory, useParams } from 'react-router-dom'

import { icdServer, normalizeCase, stringifyDate, timeDiff, useListData, usePet, useToggle } from '../../modules'

import Confirm from '../reusable/confirm'
import Receive from '../reusable/receive'

import { SingleResult } from './entrySummary'

import '@whoicd/icd11ect/style.css'

const { TabPane } = Tabs

const diagnosisToolId = (itemId, index) => `diagnosis_${String(itemId ?? 'new').replace(/[^a-zA-Z0-9]/g, '_')}_${index}`

const NewCoding = ({ onComplete, path, department }) => {
    const [coding, setCoding] = useState([{ diagnosis: null, status: null, classification: null }])
    const [catString, setCatString] = useState()

    const { id } = useParams()
    const history = useHistory()
    const [oi, setOi] = useState()

    const [visible, toggleVisible] = useToggle(true)

    const close = () => {
        toggleVisible()
        onComplete()
        setTimeout(() => history.goBack(), 300)
    }

    const addCoding = () => setCoding([...coding, { diagnosis: null, classification: null }])

    const editCoding = (i, key, val) => {
        const newCoding = [...coding]
        newCoding[i][key] = val
        setCoding(newCoding)
    }

    const removeCoding = index => setCoding(coding.filter((_, i) => i !== index))

    useEffect(() => {
        ECT.Handler.configure({
            apiServerUrl: icdServer,
            apiSecured: false,
            autoBind: false,
            popupMode: false,
            searchByCodeOrURI: true,
            language: 'en',
        }, {
            selectedEntityFunction: selectedEntity => {
                const selectedTool = String(selectedEntity?.iNo ?? '')
                const index = coding.findIndex((_, i) => diagnosisToolId(id, i) === selectedTool)
                if (index < 0) return

                editCoding(index, 'diagnosis', {
                    label: `${selectedEntity?.theCode ?? selectedEntity?.code} - ${selectedEntity?.title}`,
                    title: selectedEntity?.title,
                    code: selectedEntity?.theCode ?? selectedEntity?.code,
                    value: selectedEntity?.theCode ?? selectedEntity?.code,
                    uri: selectedEntity?.chapterOrBlockURI ?? selectedEntity?.stemURI ?? selectedEntity?.uri,
                })
                ECT.Handler.clear(selectedTool)
            },
        })
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const timer = setTimeout(() => {
            coding.forEach((_, index) => {
                ECT.Handler.bind(diagnosisToolId(id, index))
            })
        }, 100)

        return () => clearTimeout(timer)
    }, [coding.length, id])

    const [
        { loading: oLoading, error: oError, fetch },
        { list: prev = [] },
        { navigate, Paginate, page = 1, limit = 10 }
    ] = useListData('coding', { department, patient: oi?.patient?._id, string: '' }, { notify: true, manual: true })

    const refresh = () => {
        navigate(1)
        fetch()
    }

    const [
        { loading: cLoading, error: cError, fetch: fetchClassifications },
        { list: classifications = [] }
    ] = useListData('codingClassifications', { string: catString, book: timeDiff(oi?.patient?.dob) < 5 ? 'Under 5' : 'Over 5' }, { notify: true, manual: true }, 'classifications')

    const [{ loading, error }] = usePet('entriesFromOrderItem', { ref: id }, {
        notify: true, callback: ({ data }) => {
            setOi(data)
            setTimeout(() => {
                refresh()
                fetchClassifications()
            }, 200)
        }
    })

    const searchClassification = str => {
        if (catString?.trim()?.length >= 1 || !catString) {
            setCatString(str)
            fetchClassifications()
        }
    }

    const [{ loading: cdLoading }, newCoding] = usePet('newCoding', {
        item: id,
        department,
        coding: coding.map(cd => ({
            diagnosis: cd.diagnosis?.title,
            classification: cd.classification?.label,
            diagnosisCode: cd.diagnosis?.code,
            diagnosisSystem: 'ICD-11',
            status: cd?.status?.value
        })),
        age: timeDiff(oi?.patient?.dob),
        book: timeDiff(oi?.patient?.dob) < 5 ? 'Under 5' : 'Over 5',
        reattendance: coding[0]?.status?.value === 'Reattendance'
    }, {
        notify: true, callback: (_, notif, ops) => {
            notif('Coding have been saved', ops('success'))
            setTimeout(close, 200)
        }, manual: true, load: { msg: 'Saving changes...' }
    })

    const complete = () => Confirm({
        title: 'Save Coding?',
        message: 'Please confirm before performing this action',
        okText: 'Save Coding',
        onOk: newCoding
    })

    return (
        <Drawer
            closable
            maskClosable
            footer={
                <div className="w-full text-right">
                    <button onClick={complete} className="text-white hisani-btn-orange mr-3 font-medium rounded-lg text-sm px-5 py-2.5 text-center">Save Coding</button>
                    <button onClick={close} className="text-white bg-gray-500 hover:bg-gray-600 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center">Close</button>
                </div>
            }
            visible={visible}
            onClose={close}
            width="50%">
            <p className="font-extrabold text-3xl mb-0">Code {normalizeCase(oi?.patient?.name?.split(' ')[0] ?? 'Patient')}'s Visit</p>
            <p className="text-gray-500 text-xs italic"><b>{normalizeCase(oi?.patient?.name ?? 'Patient')}'s</b> visit for <b>{oi?.name}</b> {oi?.date && `on ${stringifyDate(oi.date)}`}</p>
            <Tabs tabBarExtraContent={{ right: <Button onClick={addCoding} className="tabs-extra-demo-button">Add New Coding</Button> }}>
                {
                    coding.map((c, i) =>
                        <TabPane
                            key={i}
                            tab={<span>{i === 0 ? 'Primary Coding' : `Coding ${i}`}{i > 0 && <span className='text-red-500' onClick={() => removeCoding(i)}> x </span>}</span>}>
                            <div className="flex flex-wrap -mx-3 mb-3">
                                <div className="w-full md:w-2/6 px-3 mb-6 md:mb-0">
                                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                        Diagnosis (ICD-11)
                                    </label>
                                    <input
                                        type="text"
                                        autoComplete="off"
                                        className="ctw-input embeddedBrowserSearchbox apiInitializationCompleted appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4"
                                        data-ctw-ino={diagnosisToolId(id, i)}
                                        placeholder="Search ICD-11 diagnoses"
                                    />
                                    <div className="ctw-window mt-2" data-ctw-ino={diagnosisToolId(id, i)} />
                                </div>
                                <div className="w-full md:w-2/6 px-3 mb-6 md:mb-0">
                                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-categories">
                                        Classifications
                                    </label>
                                    <Select
                                        placeholder="Search Classifications"
                                        onInputChange={searchClassification}
                                        onChange={val => editCoding(i, 'classification', val)}
                                        isLoading={cLoading}
                                        value={c.classification}
                                        options={classifications.map(cat => ({ label: cat.name, value: cat._id }))} />
                                </div>
                                {
                                    i === 0 &&
                                    <div className="w-full md:w-2/6 px-3 mb-6 md:mb-0">
                                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-status">
                                            Visit Status
                                        </label>
                                        <Select
                                            value={c.status}
                                            onChange={val => editCoding(i, 'status', val)}
                                            options={['New Visit', 'Reattendance'].map(item => ({ label: item, value: item }))}
                                            placeholder="Select Visit Status" />
                                    </div>
                                }
                            </div>
                            <div className='flex justify-between'>
                                <p className="text-gray-500 text-xs italic">Selected diagnosis: {c.diagnosis ? `${c.diagnosis?.title} (${c.diagnosis?.code})` : 'None'}</p>
                                <p className="text-gray-500 text-xs italic">Book: {timeDiff(oi?.patient?.dob) < 5 ? 'Under 5' : 'Over 5'}</p>
                            </div>
                        </TabPane>
                    )
                }
            </Tabs>
            <div className="mb-8">
                <Tabs>
                    <TabPane key="0" tab="Visit Details">
                        <Receive
                            progress
                            loading={loading}
                            error={error}>
                            {
                                oi ?
                                    oi?.entries.length > 0 ?
                                        oi.entries.map(entry =>
                                            <div className="mb-1 border-b-0 border-gray-500">
                                                {
                                                    (entry?.item?.fields ?? []).map(field =>
                                                        <SingleResult
                                                            entry={entry}
                                                            field={field} />
                                                    )
                                                }
                                                {entry.filled && <p className="text-gray-500 mt-0 text-xs italic">Recorded by {entry?.filledBy?.name} on {stringifyDate(entry?.filledOn)}</p>}
                                            </div>
                                        )
                                        :
                                        <div className="w-full h-16 mt-4 flex items-center justify-center">
                                            <p className="font-semibold text-base">Nothing has been recorded yet</p>
                                        </div>
                                    : null
                            }
                        </Receive>
                    </TabPane>
                    <TabPane key="1" tab="Previous Coding">
                        <Receive
                            error={error}
                            loading={loading}
                            progress>
                            {
                                prev.length > 0 ?
                                    <table className="table-auto w-full">
                                        {/* Table header */}
                                        <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                            <tr>
                                                <th className="p-2">
                                                    <div className="font-semibold text-left">No</div>
                                                </th>
                                                <th className="p-2">
                                                    <div className="font-semibold text-left">Visit Details</div>
                                                </th>
                                                <th className="p-2">
                                                    <div className="font-semibold text-left">Diagnosis</div>
                                                </th>
                                                <th className="p-2">
                                                    <div className="font-semibold text-left">Recorded By</div>
                                                </th>
                                            </tr>
                                        </thead>
                                        {/* Table body */}
                                        <tbody className="text-sm font-medium divide-y divide-gray-100">
                                            {/* Row */}
                                            {
                                                prev.map((cd, key) =>
                                                    <tr>
                                                        <td className="p-2">
                                                            <div className="text-left">{((page - 1) * limit) + key + 1}.</div>
                                                        </td>
                                                        <td className="p-2">
                                                            <div className="">{cd?.item?.name}</div>
                                                            <p className={`text-left text-xs italic mb-0 ${cd?.reattendance ? 'text-yellow-500' : 'text-green-500'}`}>{cd?.reattendance ? 'Reattendance' : 'New Visit'}</p>
                                                        </td>
                                                        <td className="p-2">
                                                            <div className="text-left">{cd?.diagnosis} {cd?.diagnosisCode ? `(${cd?.diagnosisCode})` : ''}</div>
                                                            <p className="text-gray-500 text-xs italic mb-0">Recorded on {stringifyDate(cd.recordedOn)}</p>
                                                        </td>
                                                        <td className="p-2">
                                                            <div className="text-left">{cd?.recordedBy?.name}</div>
                                                        </td>
                                                    </tr>
                                                )
                                            }
                                        </tbody>
                                    </table>
                                    :
                                    <div className="w-full h-16 flex items-center justify-center">
                                        <p className="font-semibold text-base">No previous coding has been recorded for {normalizeCase(oi?.patient?.name?.split(' ')[0] ?? 'Patient')}</p>
                                    </div>
                            }
                        </Receive>
                        {Paginate}
                    </TabPane>
                </Tabs>
            </div>
        </Drawer>
    )
}

export default NewCoding
